import { Children, cloneElement, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown } from "react-icons/fi";
import { ActionSelectWrapper } from "./action-select.styles";

export const ActionSelect = ({ children, className = "", closeOnSelect = true, disabled = false, isPlaceholder = false, label, menuClassName = "", onOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const selectRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      const clickedTrigger = selectRef.current?.contains(event.target);
      const clickedMenu = menuRef.current?.contains(event.target);
      if (!clickedTrigger && !clickedMenu) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return undefined;
    }

    const updateMenuPosition = () => {
      const trigger = selectRef.current?.getBoundingClientRect();
      const menu = menuRef.current?.getBoundingClientRect();
      if (!trigger || !menu) return;

      const viewportPadding = 12;
      const menuGap = 8;
      const spaceBelow = window.innerHeight - trigger.bottom;
      const spaceAbove = trigger.top;
      const openAbove = spaceBelow < menu.height + menuGap && spaceAbove > spaceBelow;
      const maximumLeft = Math.max(viewportPadding, window.innerWidth - menu.width - viewportPadding);
      const left = Math.min(Math.max(viewportPadding, trigger.left), maximumLeft);
      const availableHeight = Math.max(120, (openAbove ? spaceAbove : spaceBelow) - menuGap - viewportPadding);

      setMenuPosition({
        left,
        maxHeight: availableHeight,
        top: openAbove ? Math.max(viewportPadding, trigger.top - Math.min(menu.height, availableHeight) - menuGap) : trigger.bottom + menuGap,
      });
    };

    updateMenuPosition();
    const animationFrame = window.requestAnimationFrame(updateMenuPosition);
    window.addEventListener("resize", updateMenuPosition);
    document.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updateMenuPosition);
      document.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  const closeMenu = () => setIsOpen(false);
  const options = Children.toArray(children);
  const portalClassName = selectRef.current?.className || `${className} ${isPlaceholder ? "is-placeholder" : ""}`;
  const menu = isOpen && typeof document !== "undefined" ? createPortal(
    <div className={portalClassName}>
      <div
        ref={menuRef}
        className={`action-select-menu ${menuClassName}`}
        onClick={closeOnSelect ? closeMenu : undefined}
        role="menu"
        style={{
          left: menuPosition?.left,
          maxHeight: menuPosition?.maxHeight,
          position: "fixed",
          top: menuPosition?.top,
          zIndex: 1100,
        }}
      >
        {options.map((option) => <div key={option.key} role="none">
          {cloneElement(option, {
            onSelect: () => {
              option.props.onSelect?.();
              if (closeOnSelect) closeMenu();
            },
          })}
        </div>)}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <ActionSelectWrapper ref={selectRef} className={`${className} ${isPlaceholder ? "is-placeholder" : ""}`}>
      <button type="button" className="action-select-trigger" disabled={disabled} aria-expanded={isOpen} aria-haspopup="menu" onClick={() => {
        setIsOpen((current) => !current)
        if(!isOpen) {onOpen?.()}
      }}>
        <span className="action-select-label">{label}</span><FiChevronDown aria-hidden="true" />
      </button>
      {menu}
    </ActionSelectWrapper>
  );
};
